import { useCallback, useEffect, useState, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { useNavigate } from 'react-router-dom';
import { isEqual } from 'lodash';
import { ReportIcon } from 'src/components/ui-icons/sidebar';
import ChartLicense from '../chart-license/chart-license';
import { LabelsLicense, LabelsActions } from '../../mocks/documents';
import PanelStatus from '../panel-status/panel-status';
import { filterConfigReportLic, initialFilterOpenReportLic, initialFilterReportLic } from '../dataForFilters/reportLicense/filtersDataReportLicenses';
import { modifiedItemsState } from 'src/modules/spItems/selectors';
import FilterForPlanAction from 'src/pages/DashboardPage/vpo/components/planAction/filterForPlanAction/FilterForPlanAction';
import { myTaskActionsListName } from 'src/api/config/myTasksActions.config';
import { generateLicenses } from '../../utils/utils';
import { getItemsRequest } from 'src/modules/spItems/api';
import { AdditionalFilters, FilterValue, LicenseFilterKey, SelectionFilterValue } from '../../types/typesGos';
import { transformActionsToChartData, transformLicensesToChartData } from '../../utils/filterLicenseChartData';
import { selectUsers } from 'src/modules/users/selector';
import { selectModelData } from '../../store/filterData/selector';
import { fetchData } from '../../store/filterData/actions';
import { transformDataForFilters, transformDataForFiltersInCode } from '../../utils/utilsGOS';
import React from 'react';

interface Filters {
  [key: string]: FilterValue;
}

function ReportLicense() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [currentFilters, setCurrentFilters] = useState<Filters>(initialFilterReportLic);
  const [prevFilters, setPrevFilters] = useState<Filters>(initialFilterReportLic);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterChangeCounter, setFilterChangeCounter] = useState(0);
  const [localLicenses, setLocalLicenses] = useState<any[]>([]);
  const [localActions, setLocalActions] = useState<any[]>([]);

  const [isLoadingActions, setIsLoadingActions] = useState(false);
  const [getActions, setGetActions] = useState(false);
  const [nextPageActions, setNextPageActions] = useState<SP.ListItemCollectionPosition | undefined>(undefined);

  const typeActivityFilterData = useAppSelector((state: any) => selectModelData(state, 'gos-activities'));
  const typeDocumentFilterData = useAppSelector((state: any) => selectModelData(state, 'gos-document-types'));
  const licenseStatusFilterData = useAppSelector((state: any) => selectModelData(state, 'gos-license-statuses'));
  const locationMappingData = useAppSelector((state: any) => selectModelData(state, 'gos-locations'));
  const licensesData = useAppSelector((state: any) => selectModelData(state, 'gos-licenses'));

  const licensesLoading = useAppSelector((state: any) => state.gosStrapi.filtersData['gos-licenses']?.loading);
  const licensesPagination = useAppSelector((state: any) => state.gosStrapi.filtersData['gos-licenses']?.pagination);

  const hasNextPage = licensesPagination ? licensesPagination.page < licensesPagination.pageCount : false;

  const users: any = useAppSelector(selectUsers);

  const itemsPerPage = 75;

  const filterMappings = {
    licenses: {
      typeActivity: 'TypeActivity',
      typeDocument: 'DocumentType',
    },
  };

  //! Получаю локации, статусы лицензий, тип документа и тип деятельности
        useEffect(() => {
          dispatch(
            fetchData(
              'gos-locations',
              {},
              {
                page: 1,
                pageSize: 100,
              }
            )
          );
          dispatch(
            fetchData(
              'gos-license-statuses',
              {},
              {
                page: 1,
                pageSize: 100,
              }
            )
          );
          dispatch(
            fetchData(
              'gos-document-types',
              {},
              {
                page: 1,
                pageSize: 100,
              }
            )
          );
            dispatch(
            fetchData(
              'gos-activities',
              {},
              {
                page: 1,
                pageSize: 100,
              }
            )
          );
        }, [dispatch]);

        const locationMapping = React.useMemo(() => transformDataForFiltersInCode(locationMappingData), [locationMappingData]);
        const licenseStatusFilter = React.useMemo(() => transformDataForFilters(licenseStatusFilterData), [licenseStatusFilterData]);
        const typeDocumentFilter = React.useMemo(() => transformDataForFilters(typeDocumentFilterData), [typeDocumentFilterData]);
        const typeActivityFilter = React.useMemo(() => transformDataForFilters(typeActivityFilterData), [typeActivityFilterData]);

  const getFormattedFilters = useCallback(() => {
    const additionalFilters: AdditionalFilters = {};

    if (currentFilters.responsible?.selected && currentFilters.responsible?.selected?.length > 0) {
      additionalFilters.ResponsibleEmail = currentFilters.responsible.selected;
    }

    function isFilterKey(key: string): key is LicenseFilterKey {
      return key in currentFilters;
    }

    function isSelectionFilter(filter: FilterValue): filter is SelectionFilterValue {
      return 'selected' in filter;
    }

    Object.entries(filterMappings.licenses).forEach(([filterKey, mappedKey]) => {
      if (isFilterKey(filterKey) && currentFilters[filterKey] && isSelectionFilter(currentFilters[filterKey]) && currentFilters[filterKey].selectedIds?.length > 0) {
        additionalFilters[mappedKey] = currentFilters[filterKey].selectedIds;
      }
    });

    return {
      mainFilters: { ...additionalFilters },
    };
  }, [currentFilters]);

  //! изменение фильтров
  useEffect(() => {
    const filtersChanged = JSON.stringify(currentFilters) !== JSON.stringify(prevFilters);

    if (filtersChanged) {
      setPrevFilters(currentFilters);
      setCurrentPage(1);
      setFilterChangeCounter((prev) => prev + 1);
      setLocalLicenses([]);
    }
  }, [currentFilters, prevFilters]);

  //! Загрузка данных licenses при изменении фильтров
  useEffect(() => {
    if (filterChangeCounter > 0) {
      const mainFilters = getFormattedFilters();
      const isAppend = currentPage > 1;

      dispatch(
        fetchData(
          'gos-licenses',
          mainFilters.mainFilters,
          {
            page: currentPage,
            pageSize: itemsPerPage,
          },
          isAppend
        )
      );
    }
  }, [dispatch, filterChangeCounter, currentPage, getFormattedFilters, itemsPerPage]);

  useEffect(() => {
    setFilterChangeCounter(1);
  }, []);

  //! загрузка следующей страницы licenses
  useEffect(() => {
    if (!licensesLoading && hasNextPage && currentPage > 0) {
      setCurrentPage((prev) => prev + 1);
      setFilterChangeCounter((prev) => prev + 1);
    }
  }, [licensesLoading, hasNextPage, currentPage]);

  useEffect(() => {
    if (licensesLoading || hasNextPage) return;
    if (licensesData && licensesData.length > 0) {
      const sortedData = [...licensesData].sort((a, b) => b.id - a.id);
      setLocalLicenses(sortedData);
    }
  }, [licensesData, licensesLoading, hasNextPage]);

  useEffect(() => {
    if (!isLoadingActions && (!getActions || nextPageActions)) {
      setIsLoadingActions(true);

      getItemsRequest({
        listName: myTaskActionsListName,
        filters: { ImportantHashTag: `#${'Лицензии'}*` },
        rowLimit: itemsPerPage,
        nextPage: getActions ? nextPageActions : undefined,
      })
        .then((results) => {
          const { items, nextPage: newNextPage } = results;
          const modifiedActions = modifiedItemsState(items, myTaskActionsListName);

          if (!getActions) {
            setLocalActions(modifiedActions);
            setGetActions(true);
          } else {
            setLocalActions((prev) => [...prev, ...modifiedActions]);
          }

          setNextPageActions(newNextPage || undefined);
          setIsLoadingActions(false);
        })
        .catch((error) => {
          console.error('Ошибка при загрузке действий:', error);
          setIsLoadingActions(false);
          if (!getActions) setGetActions(true);
        });
    }
  }, [nextPageActions, isLoadingActions, getActions]);

  const handleFiltersChange = useCallback(
    (newFilters: any) => {
      if (!isEqual(currentFilters, newFilters)) {
        setCurrentFilters(newFilters);
      }
    },
    [currentFilters]
  );

  const filterConfigs = useMemo(
    () => ({
      typeActivity: { items: typeActivityFilter },
      typeDocument: { items: typeDocumentFilter },
      statusLicanse: { items: licenseStatusFilter },
    }),
    [typeActivityFilter, typeDocumentFilter, licenseStatusFilter]
  );

  const filtersMap = useMemo(
    () => ({
      typeActivity: typeActivityFilter,
      typeDocument: typeDocumentFilter,
      statusLicanse: licenseStatusFilter,
    }),
    [typeActivityFilter, typeDocumentFilter, licenseStatusFilter]
  );

  const { generatedLicenses, licensesChartData, actionsChartData } = useMemo(() => {
    const generated = generateLicenses(localLicenses, localActions, users);
    return {
      generatedLicenses: generated,
      licensesChartData: transformLicensesToChartData(generated, currentFilters, locationMapping),
      actionsChartData: transformActionsToChartData(generated, currentFilters, locationMapping),
    };
  }, [localLicenses, localActions, currentFilters, users, locationMapping]);

  return (
    <div className='flex justify-between w-full h-full'>
      <div className='w-[320px] mr-2 bg-white'>
        <FilterForPlanAction
          onFiltersChange={handleFiltersChange}
          filterConfigs={filterConfigs}
          filtersMap={filtersMap}
          initialFilters={initialFilterReportLic}
          filtersOpen={initialFilterOpenReportLic}
          filtersConfig={filterConfigReportLic}
          responsibleFil={true}
          heightFilter={'h-[calc(100vh-10px)]'}
        />
      </div>
      <div className='flex-1 min-w-0 m-6'>
        <div className='flex items-center text-gray-600 mb-6'>
          <span className='pr-3'>
            <ReportIcon />
          </span>
          <span className='cursor-pointer text-base' onClick={() => navigate('/dashboard/4/reports')}>
            Отчеты
          </span>
          <span className='mx-2'>→</span>
          <span className='text-base'>Отчет по лицензиям</span>
        </div>
        <div className='min-w-0'>
          <h2 className='font-medium mb-4'>Статус по лицензиям</h2>
          <PanelStatus
            data={generatedLicenses}
            isLicenses
            filters={{
              statusLicanse: currentFilters.statusLicanse.selected,
              status: currentFilters.status?.selected || [],
            }}
          />
          <ChartLicense data={licensesChartData} labels={LabelsLicense} />
          <h2 className='font-medium my-4'>Статус действий по лицензиям</h2>
          <ChartLicense data={actionsChartData} labels={LabelsActions} />
        </div>
      </div>
    </div>
  );
}

export default ReportLicense;
